import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CheckCircleIcon, AlertCircleIcon, MinusCircleIcon } from 'lucide-react';
import { toast } from 'sonner';
interface ExtractedField {
  label: string;
  value: string;
  confidence: 'high' | 'medium' | 'low';
}
interface FieldGroup {
  title: string;
  fields: ExtractedField[];
}
interface FieldExtractionProps {
  fileName: string;
  onBack: () => void;
  onSave: (fieldCount: number) => void;
}
export function FieldExtraction({
  fileName,
  onBack,
  onSave
}: FieldExtractionProps) {
  const [fieldGroups, setFieldGroups] = useState<FieldGroup[]>([{
    title: 'Personal Information',
    fields: [{
      label: 'Full Name',
      value: 'John Michael Anderson',
      confidence: 'high'
    }, {
      label: 'First Name',
      value: 'John',
      confidence: 'high'
    }, {
      label: 'Middle Name',
      value: 'Michael',
      confidence: 'medium'
    }, {
      label: 'Last Name',
      value: 'Anderson',
      confidence: 'high'
    }, {
      label: 'Date of Birth',
      value: '03/15/1985',
      confidence: 'high'
    }, {
      label: 'Place of Birth',
      value: 'Chicago, Illinois',
      confidence: 'medium'
    }, {
      label: 'Gender',
      value: 'Male',
      confidence: 'high'
    }, {
      label: 'Nationality',
      value: 'United States',
      confidence: 'high'
    }, {
      label: 'Marital Status',
      value: 'Married',
      confidence: 'low'
    }, {
      label: 'ID Number',
      value: 'DL-4829-3847-2910',
      confidence: 'medium'
    }]
  }, {
    title: 'Contact Information',
    fields: [{
      label: 'Email Address',
      value: 'john.anderson@email.com',
      confidence: 'high'
    }, {
      label: 'Secondary Email',
      value: 'j.anderson@work.com',
      confidence: 'low'
    }, {
      label: 'Phone Number',
      value: '+1 (555) 123-4567',
      confidence: 'high'
    }, {
      label: 'Mobile Number',
      value: '+1 (555) 987-6543',
      confidence: 'medium'
    }, {
      label: 'Street Address',
      value: '742 Evergreen Terrace',
      confidence: 'high'
    }, {
      label: 'City',
      value: 'Springfield',
      confidence: 'high'
    }, {
      label: 'State / Province',
      value: 'Illinois',
      confidence: 'high'
    }, {
      label: 'Postal Code',
      value: '62701',
      confidence: 'medium'
    }, {
      label: 'Country',
      value: 'United States',
      confidence: 'high'
    }]
  }, {
    title: 'Document Details',
    fields: [{
      label: 'Document Type',
      value: 'Driver License',
      confidence: 'high'
    }, {
      label: 'Document Number',
      value: 'DL-4829-3847-2910',
      confidence: 'high'
    }, {
      label: 'Issuing Authority',
      value: 'Illinois Secretary of State',
      confidence: 'medium'
    }, {
      label: 'Issue Date',
      value: '01/10/2023',
      confidence: 'high'
    }, {
      label: 'Expiration Date',
      value: '01/10/2028',
      confidence: 'low'
    }, {
      label: 'Class / Category',
      value: 'Class D',
      confidence: 'medium'
    }, {
      label: 'Restrictions',
      value: 'Corrective Lenses',
      confidence: 'low'
    }]
  }, {
    title: 'Employment Information',
    fields: [{
      label: 'Employer Name',
      value: 'Acme Corporation',
      confidence: 'medium'
    }, {
      label: 'Job Title',
      value: 'Senior Software Engineer',
      confidence: 'medium'
    }, {
      label: 'Department',
      value: 'Product Engineering',
      confidence: 'low'
    }, {
      label: 'Employee ID',
      value: 'EMP-00482910',
      confidence: 'medium'
    }, {
      label: 'Work Address',
      value: '500 Industrial Pkwy, Springfield, IL',
      confidence: 'low'
    }]
  }, {
    title: 'Financial Details',
    fields: [{
      label: 'Account Number',
      value: '****-****-4821',
      confidence: 'high'
    }, {
      label: 'Routing Number',
      value: '071000013',
      confidence: 'medium'
    }, {
      label: 'Tax ID / SSN',
      value: '***-**-1234',
      confidence: 'high'
    }, {
      label: 'Annual Income',
      value: '$120,000',
      confidence: 'low'
    }]
  }, {
    title: 'Verification & Security',
    fields: [{
      label: 'Verification Status',
      value: 'Verified',
      confidence: 'high'
    }, {
      label: 'Security Code',
      value: 'SC-9923',
      confidence: 'medium'
    }, {
      label: 'Signature Detected',
      value: 'Yes',
      confidence: 'high'
    }, {
      label: 'Photo Detected',
      value: 'Yes',
      confidence: 'high'
    }, {
      label: 'Barcode / QR',
      value: 'Present',
      confidence: 'medium'
    }]
  }]);
  const handleFieldChange = (groupIndex: number, fieldIndex: number, newValue: string) => {
    setFieldGroups(prev => {
      const updated = [...prev];
      updated[groupIndex].fields[fieldIndex].value = newValue;
      return updated;
    });
  };
  const handleSave = () => {
    const fieldCount = fieldGroups.reduce((total, group) => total + group.fields.length, 0);
    toast.success('Document fields saved successfully!', {
      description: 'All extracted information has been confirmed and saved.'
    });
    onSave(fieldCount);
  };
  const getConfidenceColor = (confidence: 'high' | 'medium' | 'low') => {
    switch (confidence) {
      case 'high':
        return 'text-green-600 bg-green-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'low':
        return 'text-red-600 bg-red-50';
    }
  };
  const getConfidenceIcon = (confidence: 'high' | 'medium' | 'low') => {
    switch (confidence) {
      case 'high':
        return <CheckCircleIcon className="w-4 h-4" data-id="element-52" />;
      case 'medium':
        return <AlertCircleIcon className="w-4 h-4" data-id="element-53" />;
      case 'low':
        return <MinusCircleIcon className="w-4 h-4" data-id="element-54" />;
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="w-full max-w-4xl mx-auto" data-id="element-55">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8" data-id="element-56">
        <div className="flex items-center justify-between mb-6" data-id="element-57">
          <div data-id="element-58">
            <h2 className="text-2xl font-semibold text-gray-900 mb-1" data-id="element-59">
              Review Extracted Fields
            </h2>
            <p className="text-gray-600 text-sm" data-id="element-60">From: {fileName}</p>
          </div>
          <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-61">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-62" />
            Back
          </button>
        </div>

        <div className="space-y-6" data-id="element-63">
          {fieldGroups.map((group, groupIndex) => <motion.div key={group.title} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: groupIndex * 0.1
        }} className="border border-gray-200 rounded-lg p-6" data-id="element-64">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-65">
                {group.title}
              </h3>
              <div className="space-y-4" data-id="element-66">
                {group.fields.map((field, fieldIndex) => <div key={field.label} className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-3 items-start" data-id="element-67">
                    <label className="text-sm font-medium text-gray-700 pt-2" data-id="element-68">
                      {field.label}
                    </label>
                    <input type="text" value={field.value} onChange={e => handleFieldChange(groupIndex, fieldIndex, e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-69" />
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${getConfidenceColor(field.confidence)}`} data-id="element-70">
                      {getConfidenceIcon(field.confidence)}
                      <span className="capitalize" data-id="element-71">{field.confidence}</span>
                    </div>
                  </div>)}
              </div>
            </motion.div>)}
        </div>

        <div className="mt-8 flex gap-3" data-id="element-72">
          <button onClick={onBack} className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-73">
            Cancel
          </button>
          <button onClick={handleSave} className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors" data-id="element-74">
            Confirm & Save
          </button>
        </div>
      </div>
    </motion.div>;
}